import { debounce, get, pick, setWith } from 'lodash';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { type ColorResult, ChromePicker } from 'react-color';
import styled, { ThemeContext } from 'styled-components';
import {
  type CustomColors,
  CUSTOM_COLORS_KEYS,
  createDivergingColors,
  createPeerGroupColors,
  ExternalActivityListener,
  RelativePortal,
  GetColor,
} from 'venn-ui-kit';
import { useSyncedState } from 'venn-utils';

export interface ColorItem {
  name: string;
  path: string[];
}

export const InlineColorPicker = ({
  color,
  setHasPendingChange,
}: {
  color: ColorItem;
  setHasPendingChange: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { Colors, setCustomColors, customColors } = useContext(ThemeContext);
  const [editing, setEditing] = useState(false);
  const [hexColor, setHexColor] = useSyncedState<string>(get(Colors, color.path));

  const debouncedSetCustomColors = useMemo(
    () =>
      debounce((customColors: CustomColors | undefined) => {
        setCustomColors(customColors);
        setHasPendingChange(false);
      }, 500),
    [setCustomColors, setHasPendingChange],
  );

  const onChange = (hexColor: string) => {
    setHasPendingChange(true);
    setHexColor(hexColor);
    const currentCustomColors = customColors ?? pick(Colors, CUSTOM_COLORS_KEYS);
    const newCustomColors = setWith({ ...currentCustomColors }, color.path, hexColor, (obj) => ({ ...obj }));
    debouncedSetCustomColors(deriveColors(newCustomColors));
  };

  const deriveColors = (newCustomColors: CustomColors): CustomColors => {
    const { DivergingColor, PeerGroupColor } = newCustomColors;
    return {
      ...newCustomColors,
      DivergingColor: createDivergingColors(DivergingColor.A5, DivergingColor.B5, DivergingColor.MID),
      PeerGroupColor: createPeerGroupColors(PeerGroupColor.GradientHigh),
    };
  };

  const portalClassName = `inline-color-picker-portal-${color.path.join('-')}`;

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }} data-testid="qa-selected-color">
      <ExternalActivityListener
        listeningEnabled={editing}
        onExternalActivity={() => setEditing(false)}
        ignoreActivityFromClassName={portalClassName}
      >
        <ColorPatch aria-label={color.name} color={hexColor} onClick={() => setEditing(true)} highlighted={editing} />
      </ExternalActivityListener>
      {editing && (
        <RelativePortal expectedHeight={260} leftOffset={-40} topOffset={15} className={portalClassName}>
          <ColorSwatch color={hexColor} onChange={onChange} />
        </RelativePortal>
      )}
      <span style={{ paddingLeft: '8px' }}>
        {color.name}
        <span style={{ color: Colors.GreyScale.Grey80 }}>&nbsp;({hexColor})</span>
      </span>
    </div>
  );
};

const ColorSwatch = ({ color, onChange }: { color: string; onChange: (newColor: string) => void }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const onChangeComplete = (newColor: ColorResult) => {
    const hexColor = newColor.hex.toUpperCase();
    onChange(hexColor);
  };

  useEffect(() => {
    const hexInput = wrapperRef.current?.querySelector('input');
    hexInput?.focus();
    hexInput?.select();
  }, []);

  return (
    <div ref={wrapperRef}>
      <ChromePicker color={color} onChangeComplete={onChangeComplete} />
    </div>
  );
};

const ColorPatch = styled.button<{ color: string; highlighted: boolean }>`
  width: 40px;
  height: 28px;
  border-radius: 2px;
  background-color: ${(props) => props.color};
  border: 2px solid ${({ highlighted }) => (highlighted ? GetColor.Primary.Main : 'transparent')};
  margin-left: 5px;
`;
